// Navigation
export const navLinks = [
  { label: "Home", path: "/" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "About", path: "/about" },
  { label: "Our Story", path: "/our-story" },
  { label: "Inside Lifely", path: "/inside-lifely" },
  { label: "Pricing", path: "/pricing" },
  { label: "Contact", path: "/contact" },
];

export const APP_URL = "https://app.lifely.guide";

// Hero
export const heroContent = {
  heading: "Map what matters.",
  headingAccent: "Keep context current.",
  subheading:
    "Lifely is a clear, secure way to organise and document the important details, intentions, and continuity preferences that shape your life — without storing sensitive secrets or executing actions on your behalf.",
  primaryCta: { label: "View pricing", path: "/pricing" },
  secondaryCta: { label: "Members area", url: "https://app.lifely.guide" },
};

// Problem section
export const problemContent = {
  heading: "Life changes faster than documents do.",
  body: "Assets, accounts, responsibilities, and relationships can change many times between formal updates to a will or legal document. Lifely helps you capture orientation and intent as life evolves \u2014 without turning every change into a legal exercise.",
};

// Crypto section
export const cryptoContent = {
  heading: "Some assets rely on knowledge, not institutions.",
  body: "With self-custodied digital assets, there may be no administrator, no reset process, and no recovery desk. If critical context is lost, it may not be possible to reconstruct later. Lifely helps by recording what exists, what matters, and where access is intentionally managed \u2014 without storing passwords, seed phrases, or private keys.",
  bullets: [
    "Map wallets, chains, staking, and protocol positions",
    "Highlight what\u2019s critical vs what can be ignored",
    "Record access pathways (e.g. \u2018stored in 1Password\u2019) without storing secrets",
  ],
};

// How it works
export const howItWorksSteps = [
  {
    number: "01",
    title: "Map",
    description:
      "Create structured maps across crypto, investments, property, vehicles, pets, online accounts, and physical records.",
  },
  {
    number: "02",
    title: "Context",
    description:
      "Add intent and notes that help someone understand what matters and what not to worry about.",
  },
  {
    number: "03",
    title: "Continuity",
    description:
      "Set gentle check-ins and choose what happens if you stop responding — like sharing maps with a trusted contact or partner to view selected information from Lifely.",
  },
];

// Trust section
export const trustContent = {
  heading: "Designed for clarity \u2014 not custody.",
  is: [
    "An orientation map",
    "A living companion to formal documents",
    "A place to keep important context together",
  ],
  isNot: [
    "A crypto wallet or exchange",
    "A password manager or vault",
    "A legal service or a replacement for a will",
    "A system that executes transactions",
  ],
};

// Plans
export const plansData = [
  {
    name: "Lifely Insights",
    tagline: "Learn & prepare",
    price: "Free",
    priceNote: "14-day access",
    description: "Guides and best practices — no personal data storage.",
    features: [
      "Global estate planning guides",
      "Crypto continuity education",
      "Structured planning checklists",
      "Cross-jurisdiction insights",
      "Practical frameworks in plain English",
      "Ongoing updates as rules evolve",
    ],
    cta: "Join Insights",
    note: "No personal data storage.",
    highlighted: false,
    memberstackPriceId: "prc_free-access-68000lg",
    isPaid: false,
  },
  {
    name: "Lifely Standard",
    tagline: "Map & maintain",
    price: "$5",
    priceNote: "/month, billed annually",
    description: "Maps, continuity check-ins, and printable summaries.",
    features: [
      "Everything in Insights, plus:",
      "Maps (crypto, investments, property, vehicles, pets, online accounts, physical records)",
      "Priority marking (High / Critical first)",
      "Continuity check-ins",
      "Printable summaries (Print / Save as PDF)",
    ],
    cta: "Start Standard",
    note: "No passwords, seed phrases, or private keys stored.",
    highlighted: true,
    memberstackPriceId: "prc_lifely-standard-iiby0j2c",
    isPaid: true,
  },
  {
    name: "Lifely Plus",
    tagline: "Reference & store",
    price: "Coming soon",
    priceNote: "",
    description:
      "Everything in Standard, plus optional storage for non-sensitive reference documents.",
    features: [
      "Everything in Standard",
      "Non-sensitive reference document storage",
      "E.g. policies, manuals, valuation reports",
      "SMS Continuity Check-ins",
      "Plus much more",
    ],
    cta: "Coming Soon",
    note: "Not for sensitive documents or credentials.",
    highlighted: false,
    comingSoon: true,
    memberstackPlanId: "pln_lifely-plus-aa5cs0ud6",
    isPaid: false,
  },
];

// FAQ
export const faqData = [
  {
    question: "Does Lifely store seed phrases or passwords?",
    answer:
      "No. Lifely never stores passwords, seed phrases, private keys, or sensitive credentials. You can note where these are managed (e.g. \u20181Password\u2019) without storing the actual secrets.",
  },
  {
    question: "Is Lifely a will or legal service?",
    answer:
      "No. Lifely complements your formal documents by capturing the living context that changes more often than a will. It\u2019s an orientation tool, not a legal instrument.",
  },
  {
    question: "Does Lifely custody crypto?",
    answer:
      "No. Lifely does not hold, manage, or transact any cryptocurrency. It helps you map what exists and how access is managed.",
  },
  {
    question: "Can I export my maps?",
    answer:
      "Yes. Lifely Standard and Plus include printable summaries that you can print or save as PDF for offline reference.",
  },
];

// Contact topics
export const contactTopics = [
  "General question",
  "Pricing & plans",
  "Technical support",
  "Partnerships",
  "Privacy request",
  "Feedback",
];

// Insights guides
export const insightsGuides = [
  {
    category: "Self-Custody Assets",
    guides: [
      {
        title: "Understanding Self-Custody: A Beginner\u2019s Framework",
        description:
          "Learn the basics of holding your own crypto keys and what responsibilities come with it.",
        readTime: "8 min read",
      },
      {
        title: "Mapping Your Crypto Holdings",
        description:
          "A practical guide to documenting wallets, chains, and protocol positions without storing secrets.",
        readTime: "12 min read",
      },
      {
        title: "Recovery Planning for Digital Assets",
        description:
          "How to ensure critical knowledge isn\u2019t lost if something happens to you.",
        readTime: "10 min read",
      },
    ],
  },
  {
    category: "Password & Access Management",
    guides: [
      {
        title: "Choosing a Password Manager",
        description:
          "A comparison of popular password managers and what to look for.",
        readTime: "6 min read",
      },
      {
        title: "Access Pathways: Documenting Without Storing",
        description:
          "How to create helpful references without compromising security.",
        readTime: "7 min read",
      },
    ],
  },
  {
    category: "Estate & Continuity Planning",
    guides: [
      {
        title: "Why a Will Isn\u2019t Enough",
        description:
          "Understanding the gap between legal documents and the context needed to act on them.",
        readTime: "9 min read",
      },
      {
        title: "Setting Up Continuity Check-ins",
        description:
          "A gentle approach to ensuring someone is alerted if you become unreachable.",
        readTime: "5 min read",
      },
      {
        title: "Talking to Family About Digital Inheritance",
        description:
          "Practical advice for a conversation nobody wants to have.",
        readTime: "11 min read",
      },
    ],
  },
  {
    category: "General Best Practices",
    guides: [
      {
        title: "The Priority Framework: What Matters Most",
        description:
          "How to decide what to document first and what can wait.",
        readTime: "6 min read",
      },
      {
        title: "Annual Review Checklist",
        description:
          "A yearly routine for keeping your maps current and relevant.",
        readTime: "4 min read",
      },
    ],
  },
];

// Privacy policy content
export const privacyContent = {
  lastUpdated: "August 2025",
  sections: [
    {
      title: "What We Collect",
      content:
        "When you create an account, we collect your name and email address. As you use Lifely, we store the content you enter into your maps \u2014 this includes descriptions of assets, accounts, responsibilities, and the context you add around them. If you configure continuity features, we store your check-in preferences and contact details for your nominated contacts. Lifely Plus members may upload non-sensitive reference documents such as insurance policies, vehicle manuals, or valuation reports. We explicitly prohibit uploading sensitive credentials, passwords, seed phrases, or private keys.",
    },
    {
      title: "What We Don\u2019t Collect",
      content:
        "Lifely never collects or stores passwords, seed phrases, private keys, or sensitive credentials of any kind. Payment card details are handled entirely by Stripe \u2014 we never see or store your card number. We do not track your activity across other websites.",
    },
    {
      title: "How We Use Your Data",
      content:
        "We use your data to provide and improve the Lifely service, send continuity check-in emails and reminders you\u2019ve configured, respond to support requests, and maintain the security of your account. We do not use your personal map content for advertising, training, or any purpose beyond delivering the service to you.",
    },
    {
      title: "Data Sharing",
      content:
        "We work with a small number of service providers for hosting, email delivery, and payment processing. We never sell your personal data. The only sharing of your map content happens according to your own continuity configuration \u2014 for example, if you choose to invite a trusted contact to view selected information. This sharing is always initiated and controlled by you.",
    },
    {
      title: "Data Retention & Deletion",
      content:
        "You can request deletion of your account and all associated data at any time by contacting privacy@lifely.guide. Upon receiving a deletion request, we will remove your data within 30 days. We may retain anonymised, aggregated data that cannot identify you. If your account is inactive for an extended period, we may reach out before taking any action on your data.",
    },
    {
      title: "Your Rights",
      content:
        "You have the right to access, correct, or delete your personal data. Where applicable (such as under GDPR), you also have the right to data portability, to restrict processing, and to object to processing. To exercise any of these rights, contact us at privacy@lifely.guide. We will respond within 30 days.",
    },
    {
      title: "Security",
      content:
        "We implement reasonable security measures to protect your data, including encryption in transit and at rest. However, Lifely is not a vault and is not designed to store highly sensitive credentials. Users should not upload passwords, seed phrases, private keys, or documents containing such information.",
    },
    {
      title: "Contact",
      content:
        "For privacy-related questions or requests, contact us at privacy@lifely.guide. We are based in New Zealand and committed to transparent data practices.",
    },
  ],
};

// Terms of service content
export const termsContent = {
  lastUpdated: "August 2025",
  sections: [
    {
      title: "About Lifely",
      content:
        "Lifely is an informational platform that helps you document important personal information, intent, and continuity preferences. Lifely is not a legal service, financial advisor, crypto custodian, password manager, or vault. The information you store in Lifely is for your own reference and orientation purposes.",
    },
    {
      title: "Acceptable Use",
      content:
        "You agree to use Lifely for its intended purpose \u2014 mapping and documenting personal information, context, and continuity preferences. You must not store passwords, seed phrases, private keys, or other sensitive credentials in Lifely. You must not use Lifely for any illegal purpose or in any way that could damage or impair the service.",
    },
    {
      title: "Your Content",
      content:
        "You retain ownership of all content you enter into Lifely. We do not claim any rights over your maps, notes, or uploaded documents. You are responsible for the accuracy and appropriateness of the content you store. We may remove content that violates these terms.",
    },
    {
      title: "Continuity Features",
      content:
        "Lifely\u2019s continuity features allow you to configure check-ins and nominate trusted contacts. These features operate on a best-effort basis. Lifely is not a legally binding mechanism for asset transfer or estate execution. You should maintain appropriate legal documents (such as a will) alongside your use of Lifely.",
    },
    {
      title: "Account & Security",
      content:
        "You are responsible for maintaining the security of your account credentials. If you believe your account has been compromised, contact support@lifely.guide immediately. We may suspend accounts that show signs of unauthorised access.",
    },
    {
      title: "Payment & Subscriptions",
      content:
        "Paid plans are billed in advance on an annual basis. Prices may change with reasonable notice. You can cancel your subscription at any time, and your access will continue until the end of your current billing period. Refunds are handled on a case-by-case basis.",
    },
    {
      title: "Limitation of Liability",
      content:
        "Lifely is provided \u201cas is\u201d without warranties of any kind. We are not liable for any decisions made based on information stored in or produced by Lifely. Our total liability is limited to the amount you\u2019ve paid for the service in the 12 months preceding any claim.",
    },
    {
      title: "Changes to Terms",
      content:
        "We may update these terms from time to time. We\u2019ll notify you of significant changes via email or through the service. Continued use after changes take effect constitutes acceptance of the new terms.",
    },
    {
      title: "Governing Law",
      content:
        "These terms are governed by the laws of New Zealand. Any disputes will be resolved in the courts of New Zealand.",
    },
    {
      title: "Contact",
      content: "Questions about these terms? Contact us at hello@lifely.guide.",
    },
  ],
};

// Map categories for hero floating cards
export const mapCategories = [
  { label: "Crypto", icon: "Shield" },
  { label: "Property", icon: "Home" },
  { label: "Investments", icon: "TrendingUp" },
  { label: "Vehicles", icon: "Car" },
  { label: "Pets", icon: "Heart" },
  { label: "Accounts", icon: "User" },
  { label: "Records", icon: "FileText" },
  { label: "Insurance", icon: "Umbrella" },
];
